// components/Header.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Coins } from 'lucide-react';
import Menu from './Menu';
import { useAuth } from '../context/AuthContext';
import transactionService from '../services/TransactionService';

function Header() {
  const { user, signInWithGoogle, logOut } = useAuth();
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [balance, setBalance] = useState(0);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const dropdownRef = useRef(null);

  const goToProfile = () => {
    navigate('/profile');
    setShowDropdown(false);
  };

  const goToMyBrands = () => {
    navigate('/mybrands');
    setShowDropdown(false);
  };

  const goToTransactions = () => {
    navigate('/transactions');
    setShowDropdown(false);
  };

  const handleLogout = async () => {
    try {
      await logOut();
      setShowDropdown(false);
      window.location.href = '/';
    } catch (error) {
      console.error("Error logging out", error);
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatBalanceCompact = (amount) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1).replace('.0', '')}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}k`;
    }
    return amount.toString();
  };

  // Fetch user balance
  useEffect(() => {
    if (user) {
      const fetchBalance = async () => {
        const result = await transactionService.getUserBalance(user.uid);
        if (result.success) {
          setBalance(result.balance);
        }
      };
      fetchBalance();
    } else {
      setBalance(0);
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  // Handle scroll to hide/show header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header when at top or scrolling up, hide when scrolling down
      if (currentScrollY === 0) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past 100px
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <header className={`bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 px-2 sticky z-70 shadow-md transition-transform duration-300 ${isHeaderVisible ? 'top-0 translate-y-0' : '-top-full -translate-y-full'
      }`}>
      <div className="w-full flex justify-between items-center">
        <div className="flex items-center">
          <Menu />
          <Link to="/" className="flex items-center ml-3">
            <h1 className="text-xl font-bold">Cek Bonus</h1>
          </Link>
        </div>

        <div className="flex items-center">
          {user ? (
            <div className="relative" ref={dropdownRef}>
              <div
                className="flex items-center cursor-pointer hover:bg-blue-700 px-2 py-1 rounded transition-colors"
                onClick={toggleDropdown}
              >
                {/* Balance Display */}
                <span className="text-sm font-medium mr-3">
                  IDR {formatBalanceCompact(balance)}
                </span>
                {user.photoURL && !imageError ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || "User"}
                    className="w-8 h-8 rounded-full mr-0 border-2 border-white object-cover"
                    onError={handleImageError}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="bg-white rounded-full p-0.5 mr-0">
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-sm font-bold">
                      {getInitials(user.displayName || user.email)}
                    </div>
                  </div>
                )}
                <svg
                  className={`ml-1 h-4 w-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Dropdown Menu */}
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-80 border border-gray-200">
                  <button
                    onClick={goToProfile}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </button>
                  <button
                    onClick={goToMyBrands}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    My Brands
                  </button>
                  <button
                    onClick={goToTransactions}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <Coins className="h-4 w-4 mr-2" />
                    My Wallet
                  </button>
                  <div className="border-t border-gray-100"></div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={signInWithGoogle}
              className="flex items-center py-1 px-3 bg-white text-blue-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google logo"
                className="w-4 h-4 mr-2"
              />
              Sign In
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;